/**
 * Sentry Error Tracking Configuration
 *
 * Initializes Sentry for error tracking across all environments.
 * DSN is configured per environment via VITE_SENTRY_DSN.
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 * @see docs/SENTRY_SETUP.md
 */
import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry error tracking.
 * Must be called before React renders (in main.tsx).
 *
 * Does nothing if VITE_SENTRY_DSN is not set — safe for local development.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT || "development";
  const release = import.meta.env.VITE_APP_VERSION
    ? `alphacore-web@${import.meta.env.VITE_APP_VERSION}`
    : undefined;

  // Never track errors on localhost — only real environments matter
  if (environment === "local") {
    return;
  }

  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,

    // Capture 100% of errors (adjust for high-traffic production)
    sampleRate: 1.0,

    // Performance monitoring — capture 20% of transactions in production, 100% in dev/qa
    tracesSampleRate: environment === "production" ? 0.2 : 1.0,

    // Session replay — capture 10% of sessions, 100% on error
    replaysSessionSampleRate: environment === "production" ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Session replay with rage click detection for UX insights
      Sentry.replayIntegration({
        // Detect rage clicks (3+ clicks on same element within 5s)
        networkDetailAllowUrls: [window.location.origin],
      }),
    ],

    // Filter out known non-actionable errors
    beforeSend(event) {
      if (
        event.exception?.values?.some(
          (e) =>
            // Firefox SecurityError — guarded in authStorage.ts / AppErrorBoundary
            e.type === "SecurityError" ||
            e.value?.includes("The operation is insecure") ||
            // Gateway errors with raw HTML — guarded in ApiClient
            e.value?.trimStart().startsWith("<!DOCTYPE") ||
            e.value?.trimStart().startsWith("<html"),
        )
      ) {
        return null;
      }
      return event;
    },

    // Scrub sensitive data from breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Remove auth tokens from fetch/xhr breadcrumbs
      if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
        if (breadcrumb.data?.url) {
          // Redact query params that might contain tokens
          try {
            const url = new URL(breadcrumb.data.url, window.location.origin);
            url.searchParams.delete("token");
            url.searchParams.delete("access_token");
            breadcrumb.data.url = url.toString();
          } catch {
            // URL parsing failed — leave breadcrumb as-is
          }
        }
      }
      return breadcrumb;
    },
  });
}

/**
 * Set the current user context in Sentry.
 * Call after login to attach user info to error reports.
 */
export function setSentryUser(user: {
  id: string;
  email?: string;
  username?: string;
}): void {
  Sentry.setUser(user);
}

/**
 * Set UX-relevant tags for segmenting Sentry data.
 * Call after login alongside setSentryUser.
 */
export function setSentryTags(tags: {
  tenantId?: string;
  role?: string;
  isAdmin?: boolean;
}): void {
  if (tags.tenantId) Sentry.setTag("tenant_id", tags.tenantId);
  if (tags.role) Sentry.setTag("user_role", tags.role);
  if (tags.isAdmin !== undefined)
    Sentry.setTag("is_admin", String(tags.isAdmin));

  // Viewport category for responsive UX segmentation
  const width = window.innerWidth;
  const viewport = width < 768 ? "mobile" : width < 1024 ? "tablet" : "desktop";
  Sentry.setTag("viewport", viewport);
}

/**
 * Clear user context from Sentry.
 * Call on logout to stop associating errors with a user.
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/**
 * Capture an exception manually (for try/catch blocks).
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  Sentry.captureException(error, { extra: context });
}

/**
 * Capture a message (non-error event).
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
): void {
  Sentry.captureMessage(message, level);
}

/**
 * Add a breadcrumb for debugging context.
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Add a UX interaction breadcrumb with standardized categories.
 * Use at key user interaction points (form submit, filter change, tab switch, etc.)
 */
export function addUxBreadcrumb(
  action: string,
  data?: Record<string, unknown>,
): void {
  Sentry.addBreadcrumb({
    category: "ux.interaction",
    message: action,
    level: "info",
    data,
  });
}

/**
 * Wrap an async operation in a Sentry performance span.
 * Use for slow user-facing operations (uploads, API calls, evaluations).
 */
export async function withSpan<T>(
  name: string,
  op: string,
  fn: () => Promise<T>,
): Promise<T> {
  return Sentry.startSpan({ name, op }, async () => {
    return fn();
  });
}

// Re-export Sentry for direct access when needed
export { Sentry };
